import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, Box, ArrowUp, ArrowDown, 
  Minus, DollarSign, Activity, Target, AlertCircle, RefreshCw 
} from 'lucide-react';
import axios from 'axios';
import Layout from '../components/Layout';

const BoxPortfolio = () => {
  const [boxStocks, setBoxStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('expected_return');
  const [stats, setStats] = useState({
    total: 0,
    buySignals: 0,
    sellSignals: 0,
    avgReturn: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // 데이터 로딩
  useEffect(() => {
    fetchBoxStocks();
  }, []);

  // 필터 및 정렬 적용
  useEffect(() => {
    applyFilterAndSort();
  }, [boxStocks, filter, sortBy]);

  const fetchBoxStocks = async () => {
    try {
      setLoading(true);
      setError(null);

      // localStorage에서 user_id 가져오기
      const userId = localStorage.getItem('user_id');

      const response = await axios.get(
        '/analysis-api/api/box-analysis',
        {
          params: {
            limit: 500,
            ...(userId ? { user_id: userId } : {})
          }
        }
      );

      if (!response.data.success) {
        throw new Error('Box 분석 데이터 조회 실패');
      }

      const stocks = response.data.boxes || [];
      
      setBoxStocks(stocks);
      
      // 통계 계산
      const buySignals = stocks.filter(s => s.buy_signal).length;
      const sellSignals = stocks.filter(s => s.sell_signal).length;
      const avgReturn = stocks.length > 0 
        ? stocks.reduce((sum, s) => sum + parseFloat(s.expected_return || 0), 0) / stocks.length 
        : 0;
      
      setStats({
        total: stocks.length,
        buySignals,
        sellSignals,
        avgReturn: avgReturn.toFixed(2)
      });

      setLastUpdate(new Date().toLocaleString('ko-KR'));
      
    } catch (error) {
      console.error('Box 권 데이터 로딩 실패:', error);
      setError(error.response?.data?.detail || error.message || 'Box 권 데이터를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  const applyFilterAndSort = () => {
    let filtered = [...boxStocks];
    
    // 필터 적용
    switch(filter) {
      case 'BUY_SIGNAL':
        filtered = filtered.filter(s => s.buy_signal);
        break;
      case 'SELL_SIGNAL':
        filtered = filtered.filter(s => s.sell_signal);
        break;
      case 'RISING':
        filtered = filtered.filter(s => 
          s.trend_state === 'RISING' || s.trend_state === 'DECLINE_TO_RISE'
        );
        break;
      case 'DECLINING':
        filtered = filtered.filter(s => 
          s.trend_state === 'DECLINING' || s.trend_state === 'RISE_TO_DECLINE'
        );
        break;
      default:
        break;
    }
    
    // 정렬
    filtered.sort((a, b) => {
      switch(sortBy) {
        case 'expected_return':
          return parseFloat(b.expected_return || 0) - parseFloat(a.expected_return || 0);
        case 'price_position':
          return parseFloat(a.price_position || 0) - parseFloat(b.price_position || 0);
        case 'box_range':
          return parseFloat(b.box_range_pct || 0) - parseFloat(a.box_range_pct || 0);
        default:
          return 0;
      }
    });
    
    setFilteredStocks(filtered);
  };

  const getTrendIcon = (trendState) => {
    switch(trendState) {
      case 'RISING':
        return <TrendingUp className="text-green-500" size={18} />;
      case 'DECLINE_TO_RISE':
        return <ArrowUp className="text-blue-500" size={18} />;
      case 'DECLINING':
        return <TrendingDown className="text-red-500" size={18} />;
      case 'RISE_TO_DECLINE':
        return <ArrowDown className="text-orange-500" size={18} />;
      default:
        return <Minus className="text-gray-400" size={18} />;
    }
  };

  const getTrendLabel = (trendState) => {
    switch(trendState) {
      case 'RISING': return '상승 중';
      case 'DECLINE_TO_RISE': return '반등';
      case 'DECLINING': return '하락 중';
      case 'RISE_TO_DECLINE': return '하락 전환';
      default: return '중립';
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ko-KR').format(price) + '원';
  };

  const formatPercent = (value) => {
    const num = parseFloat(value || 0);
    return (num >= 0 ? '+' : '') + num.toFixed(2) + '%';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Box 권 분석 데이터를 불러오는 중...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">데이터 로딩 실패</h3>
                <p className="text-red-700 mt-1">{error}</p>
                <button
                  onClick={fetchBoxStocks}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* 헤더 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Box 권 포트폴리오</h1>
            <p className="text-gray-600 mt-1">60일 Box 권 기반 매매 시그널 분석</p>
            {lastUpdate && (
              <p className="text-sm text-gray-500 mt-1">
                마지막 업데이트: {lastUpdate}
              </p>
            )}
          </div>
          <button
            onClick={fetchBoxStocks}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            새로고침
          </button>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">전체 종목</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Box className="h-10 w-10 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">매수 시그널</p>
                <p className="text-2xl font-bold text-green-600">{stats.buySignals}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">매도 시그널</p>
                <p className="text-2xl font-bold text-red-600">{stats.sellSignals}</p>
              </div>
              <TrendingDown className="h-10 w-10 text-red-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">평균 기대수익률</p>
                <p className="text-2xl font-bold text-purple-600">{stats.avgReturn}%</p>
              </div>
              <Target className="h-10 w-10 text-purple-500" />
            </div>
          </div>
        </div>

        {/* 필터 및 정렬 */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                필터
              </label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">전체</option>
                <option value="BUY_SIGNAL">매수 시그널</option>
                <option value="SELL_SIGNAL">매도 시그널</option>
                <option value="RISING">상승 추세</option>
                <option value="DECLINING">하락 추세</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                정렬
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="expected_return">기대수익률 높은순</option>
                <option value="price_position">Box 위치 낮은순</option>
                <option value="box_range">Box 범위 큰순</option>
              </select>
            </div>
          </div>
        </div>

        {/* 종목 리스트 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">종목명</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">추세</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">현재가</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Box Top</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Box Bottom</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Box 위치</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">시그널</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">기대수익률</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStocks.map((stock) => (
                  <tr key={stock.stock_code} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {stock.stock_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {stock.stock_code}
                        </div>
                        {stock.sector_names && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {stock.sector_names.split(', ').slice(0, 2).map((s, i) => (
                              <span key={i} className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded border border-blue-100">
                                {s}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-1">
                        {getTrendIcon(stock.trend_state)}
                        <span className="text-sm text-gray-700">
                          {getTrendLabel(stock.trend_state)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatPrice(stock.current_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatPrice(stock.box_top)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatPrice(stock.box_bottom)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <span className={`font-medium ${
                        stock.price_position < 30 ? 'text-blue-600' :
                        stock.price_position > 70 ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {stock.price_position?.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center space-x-2">
                        {stock.buy_signal && (
                          <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">
                            매수
                          </span>
                        )}
                        {stock.sell_signal && (
                          <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-100 rounded-full">
                            매도
                          </span>
                        )}
                        {!stock.buy_signal && !stock.sell_signal && (
                          <span className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 rounded-full">
                            보류
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className={`text-sm font-medium ${
                        parseFloat(stock.expected_return) > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatPercent(stock.expected_return)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredStocks.length === 0 && (
            <div className="text-center py-12">
              <Box className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">필터에 맞는 종목이 없습니다</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BoxPortfolio;
